import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Mail } from "lucide-react";

const contactSchema = z.object({
  subject: z.string().min(1, "Subject is required").max(300),
  message: z.string().min(1, "Message is required").max(5000),
});
type ContactFormValues = z.infer<typeof contactSchema>;

function buildContactSchema(t: (key: string) => string) {
  return z.object({
    subject: z.string().min(1, t("nav:contact.subjectRequired")).max(300),
    message: z.string().min(1, t("nav:contact.messageRequired")).max(5000),
  });
}

interface ContactUsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName?: string;
  userEmail?: string;
}

function buildMessageTemplate(userName: string, userEmail: string): string {
  const footer = userName || userEmail
    ? `\n\n— ${[userName, userEmail ? `(${userEmail})` : ""].filter(Boolean).join(" ")}`
    : "";
  return footer;
}

export function ContactUsModal({ open, onOpenChange, userName = "", userEmail = "" }: ContactUsModalProps) {
  const { t } = useTranslation(["nav", "common"]);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(buildContactSchema(t)),
    defaultValues: {
      subject: "",
      message: buildMessageTemplate(userName, userEmail),
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        subject: "",
        message: buildMessageTemplate(userName, userEmail),
      });
      setSent(false);
      setErrorMsg(null);
    }
  }, [open, userName, userEmail]);

  useEffect(() => {
    if (!sent) return;
    const timer = setTimeout(() => {
      onOpenChange(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, [sent, onOpenChange]);

  const mutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      await apiRequest("POST", "/api/contact", {
        subject: values.subject,
        message: values.message,
      });
    },
    onSuccess: () => {
      setSent(true);
      setErrorMsg(null);
    },
    onError: (err: any) => {
      const msg = err?.message || t("nav:contact.sendError");
      setErrorMsg(msg);
    },
  });

  function handleOpenChange(val: boolean) {
    if (!val) {
      setSent(false);
      setErrorMsg(null);
      form.reset({
        subject: "",
        message: buildMessageTemplate(userName, userEmail),
      });
    }
    onOpenChange(val);
  }

  function onSubmit(values: ContactFormValues) {
    setErrorMsg(null);
    mutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-muted-foreground" />
            {t("nav:contact.title")}
          </DialogTitle>
          <DialogDescription>
            {t("nav:contact.description")}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="font-medium">{t("nav:contact.sent")}</p>
            <p className="text-sm text-muted-foreground">
              {t("nav:contact.followUp")}{" "}
              <span className="font-medium">{userEmail || t("nav:contact.yourEmail")}</span>.
            </p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("nav:contact.from")}</p>
                  <p className="font-medium truncate" data-testid="contact-sender-name">{userName || t("nav:contact.emptyValue")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t("nav:contact.email")}</p>
                  <p className="font-medium truncate" data-testid="contact-sender-email">{userEmail || t("nav:contact.emptyValue")}</p>
                </div>
              </div>

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("nav:contact.subject")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("nav:contact.subjectPlaceholder")}
                        data-testid="input-contact-subject"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("nav:contact.message")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("nav:contact.messagePlaceholder")}
                        className="min-h-[120px] resize-none"
                        data-testid="input-contact-message"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {errorMsg && (
                <p className="text-sm text-destructive" data-testid="contact-error">{errorMsg}</p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  data-testid="button-contact-cancel"
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={mutation.isPending}
                  data-testid="button-contact-send"
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("nav:contact.sending")}
                    </>
                  ) : (
                    t("nav:contact.send")
                  )}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
